import React, { useEffect, useState } from "react";
import api, { formatMoney, formatDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ArrowDownRight, ArrowUpRight, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  type: "income",
  category: "",
  description: "",
  amount: "",
  issue_date: new Date().toISOString().slice(0, 10),
  status: "paid",
};

const INCOME_CATS = ["Servis Geliri", "Danışmanlık", "Kira Geliri", "Faiz Geliri", "Diğer"];
const EXPENSE_CATS = ["Personel", "Kira", "Ofis Giderleri", "Elektrik Malzemeleri", "Yakıt", "Vergi", "Nakliye", "Diğer"];

export default function IncomeExpense() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("income");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  async function load() {
    const { data } = await api.get("/transactions");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  const income = items.filter((x) => x.type === "income");
  const expense = items.filter((x) => x.type === "expense");
  const totalIncome = income.reduce((a, b) => a + b.amount, 0);
  const totalExpense = expense.reduce((a, b) => a + b.amount, 0);

  function openNew(type) {
    setForm({ ...emptyForm, type });
    setOpen(true);
  }

  async function save() {
    try {
      if (!form.description || !form.amount) return toast.error("Zorunlu alanları doldurun");
      await api.post("/transactions", {
        ...form,
        amount: Number(form.amount),
        issue_date: new Date(form.issue_date).toISOString(),
      });
      toast.success("Kayıt eklendi");
      setOpen(false);
      setForm(emptyForm);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function remove(id) {
    if (!window.confirm("Silmek istiyor musunuz?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Silindi");
    await load();
  }

  function renderTable(rows, type) {
    if (rows.length === 0) return <div className="p-10 text-center text-slate-500">Kayıt yok</div>;
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02]">
            <tr className="text-left text-[11px] uppercase mono tracking-widest text-slate-500 border-b border-white/5">
              <th className="py-3 px-5">Tarih</th>
              <th className="py-3 px-5">Kategori</th>
              <th className="py-3 px-5">Açıklama</th>
              <th className="py-3 px-5">Tutar</th>
              <th className="py-3 px-5">Durum</th>
              <th className="py-3 px-5 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`ie-row-${t.id}`}>
                <td className="py-3 px-5 tabular text-slate-300">{formatDate(t.issue_date)}</td>
                <td className="py-3 px-5">
                  <Badge className={type === "income" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/25" : "bg-red-500/15 text-red-300 border-red-500/25"}>
                    {t.category || "Diğer"}
                  </Badge>
                </td>
                <td className="py-3 px-5 text-slate-300">{t.description}</td>
                <td className={`py-3 px-5 tabular font-semibold ${type === "income" ? "text-emerald-300" : "text-red-300"}`}>
                  {type === "income" ? "+" : "-"}{formatMoney(t.amount)}
                </td>
                <td className="py-3 px-5"><Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/25">Tamamlandı</Badge></td>
                <td className="py-3 px-5 text-right">
                  <Button size="sm" variant="ghost" onClick={() => remove(t.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ie-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400">Kayıt İşlemleri</div>
          <h1 className="text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-amber-400" /> Gelir & Gider
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Faturasız gelir ve giderlerinizi kategorize edin.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openNew("income")} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold" data-testid="add-income-btn">
            <Plus className="w-4 h-4 mr-1" /> Gelir
          </Button>
          <Button onClick={() => openNew("expense")} className="bg-red-500 hover:bg-red-400 text-white font-semibold" data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-1" /> Gider
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-elevated rounded-2xl p-5 border border-emerald-500/25">
          <div className="flex items-center gap-2 text-emerald-300 text-xs mono uppercase tracking-widest">
            <ArrowDownRight className="w-4 h-4" /> Toplam Gelir
          </div>
          <div className="text-3xl font-bold tabular mt-2 text-emerald-300">{formatMoney(totalIncome)}</div>
        </div>
        <div className="card-elevated rounded-2xl p-5 border border-red-500/25">
          <div className="flex items-center gap-2 text-red-300 text-xs mono uppercase tracking-widest">
            <ArrowUpRight className="w-4 h-4" /> Toplam Gider
          </div>
          <div className="text-3xl font-bold tabular mt-2 text-red-300">{formatMoney(totalExpense)}</div>
        </div>
        <div className="card-elevated rounded-2xl p-5 border border-amber-500/40">
          <div className="text-xs mono uppercase tracking-widest text-amber-300">Net</div>
          <div className={`text-3xl font-bold tabular mt-2 ${totalIncome - totalExpense >= 0 ? "text-amber-300" : "text-red-300"}`}>
            {formatMoney(totalIncome - totalExpense)}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#111723] border border-white/5">
          <TabsTrigger value="income" data-testid="tab-income">Gelirler ({income.length})</TabsTrigger>
          <TabsTrigger value="expense" data-testid="tab-expense">Giderler ({expense.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="income" className="mt-4"><div className="card-elevated rounded-2xl overflow-hidden">{renderTable(income, "income")}</div></TabsContent>
        <TabsContent value="expense" className="mt-4"><div className="card-elevated rounded-2xl overflow-hidden">{renderTable(expense, "expense")}</div></TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B111C] border-white/10 max-w-md" data-testid="ie-dialog">
          <DialogHeader><DialogTitle>{form.type === "income" ? "Gelir Ekle" : "Gider Ekle"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-[#111723] border-white/10 mt-1"><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
                <SelectContent className="bg-[#0B111C] border-white/10">
                  {(form.type === "income" ? INCOME_CATS : EXPENSE_CATS).map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Açıklama</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#111723] border-white/10 mt-1" data-testid="ie-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tutar (₺)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-[#111723] border-white/10 mt-1" data-testid="ie-amount-input" />
              </div>
              <div>
                <Label>Tarih</Label>
                <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">İptal</Button>
            <Button onClick={save} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="save-ie-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
